import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import Icon from './Icon';

// Shared primitives ported from design_handoff_web/wa-ui.jsx — inline styles on
// the token vars (faithful to the design references). WAPhoto is adapted to take
// a resolved `src` URL (real photos) instead of a picsum seed.

type Variant = 'primary' | 'ink' | 'secondary' | 'ghost' | 'tertiary';
type Size = 'sm' | 'md' | 'lg';

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  iconR,
  full,
  children,
  onClick,
  style,
  disabled,
  title,
  type = 'button',
}: {
  variant?: Variant;
  size?: Size;
  icon?: string;
  iconR?: string;
  full?: boolean;
  children?: ReactNode;
  onClick?: () => void;
  style?: CSSProperties;
  disabled?: boolean;
  title?: string;
  type?: 'button' | 'submit';
}) {
  const pad = { sm: '7px 12px', md: '10px 16px', lg: '13px 20px' }[size];
  const fs = { sm: 12, md: 13, lg: 14 }[size];
  const base: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    padding: variant === 'tertiary' ? '4px 2px' : pad,
    fontSize: fs,
    fontFamily: 'var(--font-ui)',
    fontWeight: 500,
    borderRadius: 'var(--r-sm)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'transparent',
    letterSpacing: '0.01em',
    transition: 'background var(--dur-fast), border-color var(--dur-fast), color var(--dur-fast), filter var(--dur-fast)',
    width: full ? '100%' : 'auto',
    whiteSpace: 'nowrap',
    lineHeight: 1.1,
    opacity: disabled ? 0.5 : 1,
  };
  const variants: Record<Variant, CSSProperties> = {
    primary: { background: 'var(--accent)', color: '#FCFAF6' },
    ink: { background: 'var(--ink)', color: 'var(--bg)' },
    secondary: { background: 'var(--surface)', color: 'var(--ink)', borderColor: 'var(--line-strong)' },
    ghost: { background: 'transparent', color: 'var(--ink-soft)' },
    tertiary: { background: 'transparent', color: 'var(--accent)' },
  };
  return (
    <button
      type={type}
      className="wa-focusable"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{ ...base, ...variants[variant], ...style }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.filter = 'brightness(0.96)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.filter = '';
      }}
    >
      {icon && <Icon name={icon} size={fs + 2} />}
      {children}
      {iconR && <Icon name={iconR} size={fs + 2} />}
    </button>
  );
}

type ChipTone = 'plain' | 'accent' | 'cat' | 'solid';
export function Chip({
  children,
  tone = 'plain',
  active,
  onClick,
  icon,
  removable,
  onRemove,
  style,
}: {
  children: ReactNode;
  tone?: ChipTone;
  active?: boolean;
  onClick?: () => void;
  icon?: string;
  removable?: boolean;
  onRemove?: () => void;
  style?: CSSProperties;
}) {
  const tones: Record<ChipTone, { background: string; color: string; border: string }> = {
    plain: { background: 'var(--surface)', color: 'var(--ink-soft)', border: 'var(--line)' },
    accent: { background: 'var(--accent-wash)', color: 'var(--accent-deep)', border: 'var(--accent-soft)' },
    cat: { background: 'var(--c-wash)', color: 'var(--c)', border: 'var(--c-soft)' },
    solid: { background: 'var(--ink)', color: 'var(--bg)', border: 'var(--ink)' },
  };
  const t = active ? tones.solid : tones[tone];
  return (
    <span
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '5px 10px',
        borderRadius: 'var(--r-pill)',
        background: t.background,
        color: t.color,
        border: `1px solid ${t.border}`,
        fontFamily: 'var(--font-mono)',
        fontSize: 11,
        letterSpacing: '0.03em',
        cursor: onClick ? 'pointer' : 'default',
        whiteSpace: 'nowrap',
        transition: 'background var(--dur-fast), color var(--dur-fast)',
        ...style,
      }}
    >
      {icon && <Icon name={icon} size={12} />}
      {children}
      {removable && (
        <Icon
          name="close"
          size={11}
          style={{ marginLeft: 1, opacity: 0.6, cursor: 'pointer' }}
        />
      )}
      {removable && onRemove && (
        <span
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          style={{ position: 'absolute', inset: 0 }}
          aria-hidden
        />
      )}
    </span>
  );
}

export function Photo({
  src,
  credit,
  radius = 'var(--r-md)',
  className = '',
  creditPos = 'bl',
  dim = 0,
  style,
  children,
  onClick,
  alt,
}: {
  src?: string;
  credit?: string;
  radius?: string;
  className?: string;
  creditPos?: string;
  dim?: number;
  style?: CSSProperties;
  children?: ReactNode;
  onClick?: () => void;
  alt?: string;
}) {
  return (
    <div
      onClick={onClick}
      className={`tb-photo ${className}`}
      role={alt ? 'img' : undefined}
      aria-label={alt}
      style={{
        position: 'relative',
        borderRadius: radius,
        overflow: 'hidden',
        backgroundImage: src ? `url(${src})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: 'var(--surface-2)',
        boxShadow: 'inset 0 0 0 1px rgba(28,24,19,0.07)',
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
    >
      {dim > 0 && (
        <div style={{ position: 'absolute', inset: 0, background: `rgba(20,16,12,${dim})` }} />
      )}
      {children}
      {credit && (
        <div
          style={{
            position: 'absolute',
            [creditPos[0] === 't' ? 'top' : 'bottom']: 6,
            [creditPos[1] === 'r' ? 'right' : 'left']: 8,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            maxWidth: '85%',
            padding: '2px 6px',
            borderRadius: 4,
            background: 'rgba(20,16,12,0.42)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            letterSpacing: '0.04em',
            color: 'rgba(252,250,246,0.92)',
            textTransform: 'uppercase',
            pointerEvents: 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <Icon name="camera" size={11} style={{ opacity: 0.8, flexShrink: 0 }} /> {credit}
        </div>
      )}
    </div>
  );
}

export function Stars({
  n,
  max = 5,
  size = 11,
  filled = '#B8893B',
  glyph = 'star',
}: {
  n: number;
  max?: number;
  size?: number;
  filled?: string;
  glyph?: string;
}) {
  return (
    <span style={{ display: 'inline-flex', gap: 1.5 }}>
      {Array.from({ length: max }).map((_, i) => (
        <Icon
          key={i}
          name={glyph}
          size={size}
          style={{ color: i < n ? filled : 'var(--line-strong)', fill: i < n ? filled : 'none' }}
        />
      ))}
    </span>
  );
}

export function Dots({ n, color = 'var(--accent)', size = 7 }: { n: number; color?: string; size?: number }) {
  return (
    <span style={{ display: 'inline-flex', gap: 2 }}>
      {Array.from({ length: n }).map((_, i) => (
        <span key={i} style={{ width: size, height: size, borderRadius: '50%', background: color }} />
      ))}
    </span>
  );
}

export interface Person {
  name: string;
  role?: string;
  tint: string;
  initials: string;
}
export function Avatar({ person, size = 28, ring }: { person: Person; size?: number; ring?: boolean }) {
  return (
    <div
      title={person.role ? `${person.name} · ${person.role}` : person.name}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        background: person.tint,
        color: '#FCFAF6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'var(--font-mono)',
        fontSize: Math.max(11, size * 0.34),
        letterSpacing: '0.02em',
        boxShadow: ring ? '0 0 0 2px var(--surface)' : 'none',
        border: '1px solid rgba(255,255,255,0.25)',
      }}
    >
      {person.initials}
    </div>
  );
}
export function AvatarStack({ people, size = 28 }: { people: Person[]; size?: number }) {
  return (
    <div style={{ display: 'flex' }}>
      {people.map((p, i) => (
        <div key={i} style={{ marginLeft: i ? -8 : 0, zIndex: people.length - i }}>
          <Avatar person={p} size={size} ring />
        </div>
      ))}
    </div>
  );
}

export function SectionHead({
  eyebrow,
  title,
  action,
  size = 'var(--t-display-s)',
  style,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  action?: ReactNode;
  size?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 16,
        marginBottom: 'var(--s-4)',
        ...style,
      }}
    >
      <div style={{ flexShrink: 0, maxWidth: '100%' }}>
        {eyebrow && <div className="wa-eyebrow" style={{ marginBottom: 5 }}>{eyebrow}</div>}
        <div className="wa-display" style={{ fontSize: size }}>{title}</div>
      </div>
      {action}
    </div>
  );
}

export function Segmented({
  options,
  value,
  onChange,
  size = 'md',
  full,
}: {
  options: Array<string | { value: string; label: string; icon?: string }>;
  value: string;
  onChange: (v: string) => void;
  size?: 'sm' | 'md';
  full?: boolean;
}) {
  const pad = size === 'sm' ? '6px 11px' : '8px 14px';
  return (
    <div
      style={{
        display: 'inline-flex',
        padding: 3,
        gap: 2,
        borderRadius: 'var(--r-sm)',
        background: 'var(--surface-2)',
        border: '1px solid var(--line)',
        width: full ? '100%' : 'auto',
      }}
    >
      {options.map((o) => {
        const v = typeof o === 'string' ? o : o.value;
        const lab = typeof o === 'string' ? o : o.label;
        const on = v === value;
        return (
          <button
            key={v}
            onClick={() => onChange(v)}
            style={{
              flex: full ? 1 : 'none',
              padding: pad,
              borderRadius: 6,
              border: 'none',
              cursor: 'pointer',
              background: on ? 'var(--surface)' : 'transparent',
              color: on ? 'var(--ink)' : 'var(--ink-mute)',
              boxShadow: on ? 'var(--shadow-card)' : 'none',
              fontFamily: 'var(--font-ui)',
              fontWeight: 500,
              fontSize: 12.5,
              letterSpacing: '0.01em',
              transition: 'background var(--dur-fast), color var(--dur-fast)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              whiteSpace: 'nowrap',
            }}
          >
            {typeof o === 'object' && o.icon && <Icon name={o.icon} size={14} />}
            {lab}
          </button>
        );
      })}
    </div>
  );
}

export function Field({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  icon,
  rows,
  suffix,
  style,
}: {
  label?: string;
  value?: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  type?: string;
  icon?: string;
  rows?: number;
  suffix?: string;
  style?: CSSProperties;
}) {
  const common: CSSProperties = {
    width: '100%',
    padding: rows ? '10px 12px' : '0 12px',
    height: rows ? 'auto' : 40,
    borderRadius: 'var(--r-sm)',
    border: '1px solid var(--line-strong)',
    background: 'var(--surface)',
    color: 'var(--ink)',
    fontFamily: 'var(--font-ui)',
    fontSize: 13.5,
    resize: 'vertical',
  };
  return (
    <label style={{ display: 'block', ...style }}>
      {label && <div className="wa-eyebrow" style={{ marginBottom: 6 }}>{label}</div>}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        {icon && (
          <Icon name={icon} size={15} style={{ position: 'absolute', left: 12, color: 'var(--ink-mute)' }} />
        )}
        {rows ? (
          <textarea
            className="wa-input"
            rows={rows}
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange && onChange(e.target.value)}
            style={{ ...common, paddingLeft: icon ? 34 : 12 }}
          />
        ) : (
          <input
            className="wa-input"
            type={type}
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange && onChange(e.target.value)}
            style={{ ...common, paddingLeft: icon ? 34 : 12 }}
          />
        )}
        {suffix && (
          <span className="wa-mono" style={{ position: 'absolute', right: 12, fontSize: 11, color: 'var(--ink-mute)' }}>
            {suffix}
          </span>
        )}
      </div>
    </label>
  );
}

// ── MonthGrid — ISO calendar clamped to a trip range (design WAMonthGrid) ────
const DOW = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function isoOf(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export function MonthGrid({
  minISO,
  maxISO,
  valueISO,
  valuesISO,
  onPick,
}: {
  minISO?: string;
  maxISO?: string;
  valueISO?: string;
  /** More picked days, for a picker that takes several (Save to Plans' candidate dates). */
  valuesISO?: readonly string[];
  onPick: (iso: string) => void;
}) {
  const seed = valueISO || minISO || isoOf(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1);
  const [ym, setYm] = useState(() => ({ y: Number(seed.slice(0, 4)), m: Number(seed.slice(5, 7)) - 1 }));
  const first = new Date(Date.UTC(ym.y, ym.m, 1));
  const startDow = (first.getUTCDay() + 6) % 7; // Monday-first
  const days = new Date(Date.UTC(ym.y, ym.m + 1, 0)).getUTCDate();
  const cells: Array<number | null> = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  const monthStr = `${ym.y}-${String(ym.m + 1).padStart(2, '0')}`;
  const minMonth = minISO?.slice(0, 7);
  const maxMonth = maxISO?.slice(0, 7);
  const canPrev = !minMonth || monthStr > minMonth;
  const canNext = !maxMonth || monthStr < maxMonth;
  const step = (delta: number) =>
    setYm((s) => {
      const nm = s.m + delta;
      return { y: s.y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 };
    });
  const navBtn: CSSProperties = {
    width: 26,
    height: 26,
    borderRadius: 6,
    border: '1px solid var(--line)',
    background: 'var(--surface)',
    color: 'var(--ink-soft)',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  return (
    <div style={{ width: 268 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div className="wa-display" style={{ fontSize: 18 }}>
          {MONTHS[ym.m]} {ym.y}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button style={{ ...navBtn, opacity: canPrev ? 1 : 0.35 }} disabled={!canPrev} onClick={() => canPrev && step(-1)}>
            <Icon name="chevL" size={13} />
          </button>
          <button style={{ ...navBtn, opacity: canNext ? 1 : 0.35 }} disabled={!canNext} onClick={() => canNext && step(1)}>
            <Icon name="chevR" size={13} />
          </button>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2, marginBottom: 4 }}>
        {DOW.map((d, i) => (
          <div key={i} className="wa-mono" style={{ textAlign: 'center', fontSize: 11, color: 'var(--ink-mute)', padding: '2px 0' }}>
            {d}
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const iso = isoOf(ym.y, ym.m, d);
          const inRange = (!minISO || iso >= minISO) && (!maxISO || iso <= maxISO);
          const on = iso === valueISO || !!valuesISO?.includes(iso);
          return (
            <button
              key={i}
              disabled={!inRange}
              onClick={() => inRange && onPick(iso)}
              style={{
                aspectRatio: '1',
                border: 'none',
                cursor: inRange ? 'pointer' : 'default',
                borderRadius: 7,
                background: on ? 'var(--accent)' : inRange ? 'var(--accent-wash)' : 'transparent',
                color: on ? '#FCFAF6' : inRange ? 'var(--accent-deep)' : 'var(--ink-mute)',
                opacity: inRange ? 1 : 0.4,
                fontFamily: 'var(--font-ui)',
                fontSize: 12.5,
                fontWeight: on ? 600 : 500,
                transition: 'background var(--dur-fast)',
              }}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── TimeChips (design WATimeChips) ───────────────────────────────────────────
export function TimeChips({
  times,
  value,
  onPick,
  unavailable = [],
}: {
  times: string[];
  value?: string;
  onPick: (t: string) => void;
  unavailable?: string[];
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {times.map((t) => {
        const off = unavailable.includes(t);
        const on = value === t;
        return (
          <button
            key={t}
            disabled={off}
            onClick={() => onPick(t)}
            style={{
              padding: '7px 12px',
              borderRadius: 'var(--r-sm)',
              cursor: off ? 'not-allowed' : 'pointer',
              border: `1px solid ${on ? 'var(--accent)' : off ? 'var(--line)' : 'var(--line-strong)'}`,
              background: on ? 'var(--accent)' : off ? 'transparent' : 'var(--surface)',
              color: on ? '#FCFAF6' : off ? 'var(--ink-mute)' : 'var(--ink)',
              opacity: off ? 0.45 : 1,
              fontFamily: 'var(--font-mono)',
              fontSize: 12,
              letterSpacing: '0.02em',
              textDecoration: off ? 'line-through' : 'none',
              transition: 'background var(--dur-fast)',
            }}
          >
            {t}
          </button>
        );
      })}
    </div>
  );
}
